import * as yup from 'yup';
import {
  Box,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Slide,
  Select, Button, FormHelperText,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { forwardRef, useState } from 'react';
import { TransitionProps } from '@mui/material/transitions';
import _ from 'lodash';
import Spinner from './Spinner';
import Utils from '../utils/Utils';
import translator from '../theme/translator.json';
import { useAppDispatch } from '../store/Hooks';
import AlertUtil from '../utils/AlertUtil';
import ErrorCodes from '../models/enums/ErrorCodes';
import FormService from '../services/FormService';
import MunicipalitesSelect from './MunicipalitesSelect';
import { getCreatableFormSpecOptions } from '../pages/forms/pexbd_v2/models/formSpecRegistries';
import FormSpecKey from '../pages/forms/pexbd_v2/models/enums/FormSpecKey';

interface Props {
  setShow: any,
  show: boolean
}

const Transition = forwardRef((
  props: TransitionProps & {
      children: React.ReactElement;
    },
  ref: React.Ref<unknown>,
) => <Slide direction="up" ref={ref} {...props} />);

const PromptCreateForm: React.FC<Props> = ({ setShow, show }) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);

  const createForms = async (v: { specKey: FormSpecKey; year: number; municipalities: string[] }, resetForm: () => void) => {
    try {
      setLoading(true);
      const { specKey, year, municipalities } = v;
      await FormService.createForm(specKey, year, municipalities);
      setLoading(false);
      setShow(false);
      resetForm();
    } catch (e: any) {
      console.log(e);
      let message = '';
      switch (e.code) {
        case ErrorCodes.ALREADY_EXISTS: {
          message = await Utils.getTranslation(translator.errorMessages.municipalityCreate.alreadyInUse);
          break;
        }
        case ErrorCodes.INVALID_ARGUMENT: {
          message = await Utils.getTranslation(translator.errorMessages.municipalityCreate.passwordToShort);
          break;
        }
        default: {
          message = e.message || await Utils.getTranslation(translator.errorMessages.general.unknown);
          break;
        }
      }
      if (message) AlertUtil.createErrorAlert(message as string, dispatch);
      setLoading(false);
    }
    setLoading(false);
  };

  return (
    <Container>
      <Formik
        initialValues={{
          municipalities: [] as string[],
          specKey: '' as FormSpecKey | '',
          year: '',
        }}
        validationSchema={yup.object({
          municipalities: yup.array().of(yup.string()).required(Utils.getTranslation(translator.formMessages.requiredField)),
          specKey: yup.string().required(Utils.getTranslation(translator.formMessages.requiredField)),
          year: yup.number().transform((value, originalValue) => (
            originalValue === '' ? undefined : Number(originalValue)
          )).required(Utils.getTranslation(translator.formMessages.requiredField)),
        })}
        onSubmit={(v, { setSubmitting, resetForm }) => {
          createForms({
            specKey: v.specKey as FormSpecKey,
            year: Number(v.year),
            municipalities: v.municipalities,
          }, resetForm);
          setSubmitting(false);
        }}
      >
        {(formikProps) => (
          <Box>
            <Form>
              <Dialog
                open={show}
                onClose={() => setShow(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                TransitionComponent={Transition}
                sx={{
                  backgroundColor: 'background.default !important',
                }}
              >
                <DialogTitle id="alert-dialog-title">
                    Nouveau formulaire
                </DialogTitle>
                <Divider />
                <DialogContent>
                  <Box sx={{
                    margin: '0 0 15px 0',
                    minWidth: '500px',
                  }}>
                    <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                      <Grid item xs={12} md={6} sx={{ mt: 2 }}>
                        <FormControl fullWidth>
                          <InputLabel>Formulaire</InputLabel>
                          <Select
                            name="specKey"
                            label="Formulaire"
                            value={formikProps.values.specKey}
                            onChange={formikProps.handleChange}
                            error={formikProps.touched.specKey && Boolean(formikProps.errors.specKey)}
                          >
                            {getCreatableFormSpecOptions().map((option) => (
                              <MenuItem key={option.specKey} value={option.specKey}>{option.label}</MenuItem>
                            ))}
                          </Select>
                          {formikProps.touched.specKey && formikProps.errors.specKey && <FormHelperText>{formikProps.errors.specKey}</FormHelperText>}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6} sx={{ textAlign: 'right', mt: 2 }}>
                        <FormControl fullWidth>
                          <InputLabel>Année étudiée</InputLabel>
                          <Select
                            name="year"
                            label="Année étudiée"
                            value={formikProps.values.year}
                            onChange={formikProps.handleChange}
                            error={formikProps.touched.year && Boolean(formikProps.errors.year)}
                          >
                            {_.range(new Date().getFullYear() - 3, new Date().getFullYear() + 7).map((year:number) => (
                              <MenuItem key={year} value={year}>{year}</MenuItem>
                            ))}
                          </Select>
                          {formikProps.touched.year && formikProps.errors.year && <FormHelperText>{formikProps.errors.year}</FormHelperText>}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sx={{ mt: 2 }}>
                        <MunicipalitesSelect />
                      </Grid>
                    </Grid>
                  </Box>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '15px',
                  }}>
                    <Button
                      variant="outlined"
                      color='error'
                      sx={{ mt: 3, mb: 2 }}
                      onClick={() => setShow(false)}
                    >
                      {Utils.getTranslation(translator.components.promptCreateMunicipality.cancel)}
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={formikProps.values.municipalities.length === 0}
                      sx={{ mt: 3, mb: 2 }}
                      onClick={() => formikProps.submitForm()}
                    >
                      Créer
                    </Button>
                  </Box>
                </DialogContent>
              </Dialog>
            </Form>
          </Box>
        )}
      </Formik>
      <Spinner show={loading}/>
    </Container>
  );
};

export default PromptCreateForm;
